import React, { useState } from "react";
import { toast } from "sonner";
import { Instagram, Twitter, Facebook, Mail, MapPin, Clock, Send } from "lucide-react";
import { submitContact } from "../lib/api";
import { BUSINESS_INFO, PHOTOGRAPHY } from "../lib/constants";
import Overline from "../components/Overline";
import Reveal from "../components/Reveal";

const Field = ({ label, id, children }) => (
  <label className="block" htmlFor={id}>
    <span className="text-[10px] tracking-overline uppercase text-elle-green/60">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message sent. We'll reply soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="pt-12 md:pt-20 pb-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Overline>Contact</Overline>
          <h1 className="font-serif-display text-6xl md:text-8xl text-elle-green mt-6 leading-[0.95]">
            Write to us.
          </h1>
          <p className="mt-6 max-w-xl text-[16px] text-elle-soft">
            For wholesale, press, or to simply share how Ell'e landed in your
            cup — we read every note.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 lg:gap-20 items-start">
          <Reveal className="md:col-span-7">
            <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Name" id="name">
                  <input
                    id="name"
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="mt-2 w-full bg-transparent border-b border-elle-green/40 focus:border-elle-green outline-none py-3 text-[17px] text-elle-green placeholder:text-elle-green/40 transition-colors"
                    placeholder="Your name"
                    required
                  />
                </Field>
                <Field label="Email" id="email">
                  <input
                    id="email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="mt-2 w-full bg-transparent border-b border-elle-green/40 focus:border-elle-green outline-none py-3 text-[17px] text-elle-green placeholder:text-elle-green/40 transition-colors"
                    placeholder="you@example.com"
                    required
                  />
                </Field>
              </div>
              <Field label="Subject" id="subject">
                <input
                  id="subject"
                  data-testid="contact-input-subject"
                  type="text"
                  value={form.subject}
                  onChange={update("subject")}
                  className="mt-2 w-full bg-transparent border-b border-elle-green/40 focus:border-elle-green outline-none py-3 text-[17px] text-elle-green placeholder:text-elle-green/40 transition-colors"
                  placeholder="Wholesale, press, a note…"
                />
              </Field>
              <Field label="Message" id="message">
                <textarea
                  id="message"
                  data-testid="contact-input-message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="mt-2 w-full bg-transparent border-b border-elle-green/40 focus:border-elle-green outline-none py-3 text-[17px] text-elle-green placeholder:text-elle-green/40 transition-colors resize-none"
                  placeholder="Tell us everything…"
                  required
                />
              </Field>
              <div className="pt-4 flex items-center gap-6">
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-elle-green text-elle-cream px-8 py-4 text-[11px] tracking-overline uppercase hover:bg-[var(--elle-green-deep)] transition-colors duration-500 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <Send size={13} />
                </button>
                {sent && (
                  <span className="text-[12px] tracking-overline uppercase text-elle-red" data-testid="contact-success-msg">
                    Received — thank you.
                  </span>
                )}
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden mb-8">
              <img src={PHOTOGRAPHY.ceremony} alt="Ethiopian coffee ceremony" className="w-full h-full object-cover" />
            </div>
            <div className="space-y-5 text-[15px]">
              <a
                href={`mailto:${BUSINESS_INFO.email}`}
                data-testid="contact-info-email"
                className="flex items-center gap-3 text-elle-green link-underline"
              >
                <Mail size={16} /> {BUSINESS_INFO.email}
              </a>
              <div className="flex items-center gap-3 text-elle-soft">
                <MapPin size={16} /> {BUSINESS_INFO.location}
              </div>
              <div className="flex items-center gap-3 text-elle-soft">
                <Clock size={16} /> {BUSINESS_INFO.hours}
              </div>
              <div className="pt-4 flex items-center gap-4">
                <a href={BUSINESS_INFO.instagram} target="_blank" rel="noreferrer" className="text-elle-green hover:text-elle-red transition-colors"><Instagram size={20} /></a>
                <a href={BUSINESS_INFO.twitter} target="_blank" rel="noreferrer" className="text-elle-green hover:text-elle-red transition-colors"><Twitter size={20} /></a>
                <a href={BUSINESS_INFO.facebook} target="_blank" rel="noreferrer" className="text-elle-green hover:text-elle-red transition-colors"><Facebook size={20} /></a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
